import React, { useState } from 'react';
import styled from 'styled-components';
import type {
  AdminMemberSearchData,
  CreateCareerBulkResult,
} from '../../../shared/dto/EntityRepository';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import {
  BlackFitBtn,
  PaperBlurFitBtn,
} from '../../../shared/component/headless/button/BtnBody';

interface CareerBulkResultProps {
  result: CreateCareerBulkResult;
  targetMember: AdminMemberSearchData;
  onDone: () => void;
}

const CareerBulkResult: React.FC<CareerBulkResultProps> = ({
  result,
  targetMember,
  onDone,
}) => {
  const [issuesOnly, setIssuesOnly] = useState(false);
  const rows = issuesOnly
    ? result.results.filter((item) => item.status === 'INVALID')
    : result.results;

  return (
    <Container aria-label="Career bulk import result">
      <Title>Import result</Title>
      <Meta>{targetMember.username} · @{targetMember.handle || '—'}</Meta>
      <Summary>
        {result.createdCount} created · {result.invalidCount} error · {result.totalCount} total
      </Summary>
      <Filters>
        <HeadlessBtn
          value="All"
          handleClick={() => setIssuesOnly(false)}
          StyledBtn={!issuesOnly ? BlackFitBtn : PaperBlurFitBtn}
        />
        <HeadlessBtn
          value={`Errors (${result.invalidCount})`}
          handleClick={() => setIssuesOnly(true)}
          StyledBtn={issuesOnly ? BlackFitBtn : PaperBlurFitBtn}
        />
      </Filters>
      <List>
        {rows.map((item) => (
          <Item key={item.rowNumber}>
            <RowMeta>Row {item.rowNumber}</RowMeta>
            <Status $invalid={item.status === 'INVALID'}>
              {item.status === 'CREATED' ? 'Created' : 'Invalid'}
            </Status>
            {item.careerId != null && <Message>Career ID {item.careerId}</Message>}
            {item.message && <Message>{item.message}</Message>}
          </Item>
        ))}
      </List>
      <Buttons>
        <HeadlessBtn value="Done" handleClick={onDone} StyledBtn={BlackFitBtn} />
      </Buttons>
    </Container>
  );
};

const Container = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Meta = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Summary = styled.div`
  padding: 14px 0 10px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Filters = styled.div`
  display: flex;
  gap: 8px;
  padding-bottom: 14px;
`;

const List = styled.div`
  min-height: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  &::-webkit-scrollbar { display: none; }
`;

const Item = styled.article`
  display: grid;
  grid-template-columns: 90px 80px 1fr;
  gap: 10px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const RowMeta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Status = styled.div<{ $invalid: boolean }>`
  color: ${({ theme, $invalid }) =>
    $invalid ? theme.colors.color_Gray_02 : theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  word-break: break-word;
  ${({ theme }) => theme.typography.Body_04};
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
`;

export default CareerBulkResult;
